import { useState } from "react";
import { Upload, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABELS = { spotify: "Listen on Spotify", apple: "Listen on Apple Podcasts", website: "Visit Website" };
const field = "w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
const label = "mb-1 block text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]";
const MAX = 300;

function Received() {
  return (
    <div className="mt-10 rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--bg-card)] p-8 text-center" data-testid="podcast-submit-success">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--accent)]/20 text-[var(--accent)]"><Check size={28} /></div>
      <p className="mt-4 text-lg font-semibold text-[var(--text-primary)]">Received. We review every submission — you'll hear back once it's live.</p>
    </div>
  );
}

export default function PodcastSubmitForm() {
  const [f, setF] = useState({ show_name: "", host: "", description: "", cover_art: "", url: "", kind: "spotify", other_label: "", owner_hint: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, cover_art: `${BACKEND}${data.url}` }));
      toast.success("Cover art uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const url_label = f.kind === "other" ? f.other_label.trim() : LABELS[f.kind];
    if (!url_label) { toast.error("Add a label for your link"); return; }
    setSaving(true);
    try {
      await api.post("/podcasts/submit", { show_name: f.show_name, host: f.host, description: f.description, cover_art: f.cover_art, url: f.url.trim(), url_label, owner_hint: f.owner_hint.trim() });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Submission failed"); }
    finally { setSaving(false); }
  };

  if (done) return <Received />;

  return (
    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="podcast-submit-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className={label}>Show name *</label><input className={field} value={f.show_name} onChange={set("show_name")} required maxLength={120} data-testid="podcast-show-name" /></div>
        <div><label className={label}>Host name</label><input className={field} value={f.host} onChange={set("host")} maxLength={120} data-testid="podcast-host-input" /></div>
      </div>
      <div>
        <label className={label}>Description *</label>
        <textarea className={`${field} min-h-[110px]`} value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value.slice(0, MAX) }))} required maxLength={MAX} data-testid="podcast-description-input" />
        <p className={`mt-1 text-right text-xs ${f.description.length >= MAX ? "text-[var(--accent-hover)]" : "text-[var(--text-muted)]"}`} data-testid="podcast-description-counter">{f.description.length}/{MAX}</p>
      </div>
      <div>
        <label className={label}>Cover art</label>
        <div className="flex items-center gap-4">
          {f.cover_art
            ? <img src={f.cover_art} alt="cover preview" className="h-24 w-24 flex-none rounded-[var(--card-radius)] border border-[var(--border-subtle)] object-cover" data-testid="podcast-cover-preview" />
            : <div className="flex h-24 w-24 flex-none items-center justify-center rounded-[var(--card-radius)] border border-dashed border-[var(--border-subtle)] text-[var(--text-muted)]">?</div>}
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-2.5 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="podcast-cover-upload">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Uploading…" : "Upload cover art"}
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} data-testid="podcast-cover-file" />
          </label>
        </div>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div><label className={label}>Show URL *</label><input type="url" className={field} value={f.url} onChange={set("url")} required placeholder="https://…" data-testid="podcast-url-input" /></div>
        <div>
          <label className={label}>Link label</label>
          <select className={field} value={f.kind} onChange={set("kind")} data-testid="podcast-label-select">
            <option value="spotify">Spotify</option>
            <option value="apple">Apple Podcasts</option>
            <option value="website">Website</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>
      {f.kind === "other" && (
        <div><label className={label}>Your button label *</label><input className={field} value={f.other_label} onChange={set("other_label")} maxLength={60} required placeholder="Listen on Pocket Casts" data-testid="podcast-other-label" /></div>
      )}
      <div>
        <label className={label}>Your Dial account (optional)</label>
        <input className={field} value={f.owner_hint} onChange={set("owner_hint")} maxLength={120} placeholder="@username or the email you sign in with" data-testid="podcast-owner-hint" />
        <p className="mt-1 text-xs text-[var(--text-muted)]">Lets us link the show to you so you can add episodes once it's live.</p>
      </div>
      <button type="submit" disabled={saving || uploading} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-8 py-3 font-bold uppercase tracking-wide text-[var(--accent-contrast)] transition-colors hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="podcast-submit-button">
        {saving ? <Loader2 size={18} className="animate-spin" /> : null} Submit for review
      </button>
    </form>
  );
}
